import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import React from 'react';
import {Field, useForm} from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetch } from '../../shared/api/v1';
import type { components } from '../../shared/api/v1/openapi.types';
import { useAuth } from '../../providers/auth/auth-provider';
import { useMessage } from '../../providers/message/message-provider';
import { RoutesEnum } from '../../constants/routes';


type LoginDto = components['schemas']['LoginDto'];
type AuthResponseDto = components['schemas']['AuthResponseDto'];

type FormValues = LoginDto;

export const SignInForm = () => {
  const navigate = useNavigate();
  const {
    storeAuth,
    closeSignInModal,
    openSignUpModal,
    openPasswordResetModal,
  } = useAuth();
  const { showError } = useMessage();
  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const loginMutation = useMutation<AuthResponseDto, Error, LoginDto>({
    mutationFn: async (data: LoginDto) => {
      const response = await fetch.POST('/auth/consumer/login', {
        body: data,
      });
      
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Ошибка при входе');
      }
      
      if (!response.data) {
        throw new Error('Ошибка при входе. Попробуйте позже.');
      }
      
      return response.data;
    },
    onSuccess: (data) => {
      storeAuth(data);
      closeSignInModal();
      navigate(`/${RoutesEnum.CABINET}`);
    },
    onError: (error) => {
      showError({
        message: error.message || 'Ошибка при входе. Проверьте правильность введенных данных.',
      });
    },
  });

  const form = useForm({
    defaultValues: {
      login: '',
      password: '',
    } as FormValues,
    onSubmit: async ({ value }) => {
      try {
        await loginMutation.mutateAsync(value);
      } catch {
        // Errors are handled in onError
      }
    },
  });
  const Subscribe = form.Subscribe;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
    >
      <div
        className="flex flex-column gap-2"
      >
        <Field
          form={form}
          name="login"
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Логин</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </div>
          )}
        </Field>
        <Field
          form={form}
          name="password"
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Пароль</label>
              <InputText
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                type="password"
              />
            </div>
          )}
        </Field>
        <div className="mt-2" />
        <Subscribe<[boolean, boolean]>
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <div className="flex flex-column gap-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                loading={isSubmitting}
              >
                Войти
              </Button>
              <Button
                type="button"
                text
                onClick={() => {
                  closeSignInModal();
                  openPasswordResetModal();
                }}
                loading={isSubmitting}
              >
                Забыли пароль?
              </Button>
              <Button
                type="button"
                text
                onClick={() => {
                  closeSignInModal();
                  openSignUpModal();
                }}
                loading={isSubmitting}
              >
                Зарегистрироваться
              </Button>
            </div>
          )}
        </Subscribe>
      </div>
    </form>
  );
}